import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import cn from 'classnames'
import type { DotLottieReactProps } from '@lottiefiles/dotlottie-react'
import type { DotLottie } from '@lottiefiles/dotlottie-web'

import Icon from '../Icon2'
import { LottiePlayerProps } from './types'
import LottiePlayerErrorBoundary from './LottiePlayerErrorBoundary'

import './LottiePlayer.css'

// Lazy so the ~107 kB dotLottie JS chunk loads only when a Lottie is actually
// viewed. The dependency is externalized in venus' Rollup build, so this stays a
// runtime dynamic import the consuming app's bundler code-splits.
// Cast: dotLottie types its component as `(props) => ReactNode`, which the repo's
// React types don't accept as a lazy `ComponentType`; the props type is preserved.
const DotLottieReact = lazy(() =>
  import('@lottiefiles/dotlottie-react').then((m) => ({
    default: m.DotLottieReact as unknown as React.ComponentType<DotLottieReactProps>
  }))
)

// Formats seconds as m:ss (Lottie clips are short; minutes stay single-digit).
const formatTime = (seconds: number): string => {
  const safe = Number.isFinite(seconds) && seconds > 0 ? seconds : 0
  const mins = Math.floor(safe / 60)
  const secs = Math.floor(safe % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const LottiePlayer: React.FC<LottiePlayerProps> = (props) => {
  const { src, loop = true, autoplay = true, testId = '', className, fallback, onError, showControls = false } = props

  const [hasError, setHasError] = useState(false)
  const [isPlaying, setIsPlaying] = useState(autoplay)
  const [currentFrame, setCurrentFrame] = useState(0)
  const [totalFrames, setTotalFrames] = useState(0)
  const [duration, setDuration] = useState(0)

  const errorFiredRef = useRef(false)
  const dotLottieRef = useRef<DotLottie | null>(null)
  // Keep the latest onError without re-subscribing the instance listeners.
  const onErrorRef = useRef(onError)
  useEffect(() => {
    onErrorRef.current = onError
  }, [onError])

  // Stable handler: flips to the fallback and fires onError exactly once.
  const handleError = useCallback((error: unknown) => {
    setHasError(true)
    if (!errorFiredRef.current) {
      errorFiredRef.current = true
      onErrorRef.current?.(error)
    }
  }, [])

  const handlePlay = useCallback(() => setIsPlaying(true), [])
  const handlePause = useCallback(() => setIsPlaying(false), [])
  const handleFrame = useCallback((event: unknown) => {
    const frame = (event as { currentFrame?: number } | undefined)?.currentFrame
    if (typeof frame === 'number') setCurrentFrame(frame)
  }, [])
  // Duration + total frames are known once the animation has loaded.
  const handleLoad = useCallback(() => {
    const instance = dotLottieRef.current
    if (instance) {
      setTotalFrames(instance.totalFrames || 0)
      setDuration(instance.duration || 0)
    }
  }, [])

  // Fresh attempt whenever the source changes. totalFrames/duration are owned by
  // the `load` handler (which re-fires per source) so they are not reset here —
  // child effects run before this one, so resetting them would clobber load.
  useEffect(() => {
    setHasError(false)
    errorFiredRef.current = false
    setIsPlaying(autoplay)
    setCurrentFrame(0)
  }, [src, autoplay])

  const attach = useCallback(
    (instance: DotLottie) => {
      instance.addEventListener('loadError', handleError)
      instance.addEventListener('renderError', handleError)
      instance.addEventListener('play', handlePlay)
      instance.addEventListener('pause', handlePause)
      instance.addEventListener('complete', handlePause)
      instance.addEventListener('frame', handleFrame)
      instance.addEventListener('load', handleLoad)
    },
    [handleError, handlePlay, handlePause, handleFrame, handleLoad]
  )

  const detach = useCallback(
    (instance: DotLottie) => {
      instance.removeEventListener('loadError', handleError)
      instance.removeEventListener('renderError', handleError)
      instance.removeEventListener('play', handlePlay)
      instance.removeEventListener('pause', handlePause)
      instance.removeEventListener('complete', handlePause)
      instance.removeEventListener('frame', handleFrame)
      instance.removeEventListener('load', handleLoad)
    },
    [handleError, handlePlay, handlePause, handleFrame, handleLoad]
  )

  const dotLottieRefCallback = useCallback(
    (instance: DotLottie | null) => {
      const previous = dotLottieRef.current
      if (previous) detach(previous)
      dotLottieRef.current = instance
      if (instance) attach(instance)
    },
    [attach, detach]
  )

  useEffect(() => {
    return () => {
      const instance = dotLottieRef.current
      if (instance) detach(instance)
    }
  }, [detach])

  const togglePlay = useCallback(() => {
    const instance = dotLottieRef.current
    if (!instance) return
    if (instance.isPlaying) {
      instance.pause()
    } else {
      instance.play()
    }
  }, [])

  const handleSeek = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const frame = Number(event.target.value)
    dotLottieRef.current?.setFrame(frame)
    setCurrentFrame(frame)
  }, [])

  const seekMax = totalFrames > 0 ? totalFrames : 1
  const currentTime = totalFrames > 0 ? (currentFrame / totalFrames) * duration : 0

  const resolvedFallback = fallback ?? (
    <div className={cn('LottiePlayer__fallback')} data-test-id={`cs-${testId}-lottie-fallback`}>
      <Icon icon="FailedPreview" version="v2" width={40} height={40} testId={`cs-${testId}-lottie-fallback-icon`} />
      <span className={cn('LottiePlayer__fallback-text')}>Preview unavailable</span>
    </div>
  )

  return (
    <div className={cn('LottiePlayer', { 'LottiePlayer--error': hasError }, className)} data-test-id={`cs-${testId}-lottie-player`}>
      {hasError ? (
        resolvedFallback
      ) : (
        <LottiePlayerErrorBoundary fallback={resolvedFallback} onError={handleError}>
          <Suspense fallback={null}>
            <DotLottieReact
              src={src}
              loop={loop}
              autoplay={autoplay}
              className="LottiePlayer__canvas"
              dotLottieRefCallback={dotLottieRefCallback}
            />
          </Suspense>
          {showControls && (
            <div className={cn('LottiePlayer__controls')} data-test-id={`cs-${testId}-lottie-controls`}>
              <button
                type="button"
                className={cn('LottiePlayer__control')}
                onClick={togglePlay}
                aria-label={isPlaying ? 'Pause animation' : 'Play animation'}
                aria-pressed={isPlaying}
                data-test-id={`cs-${testId}-lottie-toggle`}>
                <Icon
                  icon={isPlaying ? 'Pause' : 'Play'}
                  version="v2"
                  width={16}
                  height={16}
                  testId={`cs-${testId}-lottie-toggle-icon`}
                />
              </button>
              <input
                type="range"
                className={cn('LottiePlayer__seek')}
                min={0}
                max={seekMax}
                step={1}
                value={Math.min(currentFrame, seekMax)}
                onChange={handleSeek}
                aria-label="Seek animation"
                data-test-id={`cs-${testId}-lottie-seek`}
              />
              <span className={cn('LottiePlayer__time')} data-test-id={`cs-${testId}-lottie-time`}>
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>
          )}
        </LottiePlayerErrorBoundary>
      )}
    </div>
  )
}

export default React.memo(LottiePlayer)
